import type { TCarrier } from '../../../types/apps'

type TCarrierFormStringKeys =
  | 'brokerMC'
  | 'dotNumber'
  | 'mcNumber'
  | 'overrideBrokerMC'
  | 'overrideDotNumber'
  | 'overrideMcNumber'
  | 'overrideScacCode'
  | 'provider'
  | 'scacCode'

type TCarrierFormAccumulator = TCarrier & {
  overrideBrokerMC?: string
  overrideDotNumber?: string
  overrideMcNumber?: string
  overrideScacCode?: string
}

export const getTruckerToolsGroupStructure = (
  uniqueID: string | number,
  disabled?: boolean,
  integrationConfigurationID?: string
) => {
  return {
    [uniqueID]: [
      {
        disabled: disabled || false,
        integrationConfigurationID: integrationConfigurationID || '',
        isRequired: true,
        labelText: 'Carrier DOT Number',
        name: `dotNumber-${uniqueID}`,
        placeholder: 'Enter carrier DOT number'
      },
      {
        disabled: disabled || false,
        integrationConfigurationID: integrationConfigurationID || '',
        isRequired: true,
        labelText: 'MC Number',
        name: `mcNumber-${uniqueID}`,
        placeholder: 'Enter MC number'
      },
      {
        disabled: disabled || false,
        integrationConfigurationID: integrationConfigurationID || '',
        isRequired: true,
        labelText: 'SCAC Code',
        name: `scacCode-${uniqueID}`,
        placeholder: 'Enter SCAC code'
      },
      {
        disabled: disabled || false,
        helpText: 'This value is shared with the Trucker Tools team for load tracking identification.',
        integrationConfigurationID: integrationConfigurationID || '',
        isRequired: true,
        labelText: 'Provider',
        name: `provider-${uniqueID}`,
        placeholder: 'Enter provider (from Trucker Tools team)'
      },
      {
        disabled: disabled || false,
        integrationConfigurationID: integrationConfigurationID || '',
        isRequired: true,
        labelText: 'Broker MC Number',
        name: `brokerMC-${uniqueID}`,
        placeholder: 'Enter broker MC number'
      }
    ]
  }
}

export const getTruckerToolsPayloadStructure = (
  event: Record<string, string>
): { carriers: TCarrier[]; clientID: string; clientSecret: string } => {
  const result: Record<string, string | TCarrier[]> = {
    carriers: [],
    clientID: event.clientID,
    clientSecret: event.clientSecret
  }
  const carrierData: Record<string, TCarrierFormAccumulator> = {}

  for (const [key, value] of Object.entries(event)) {
    const match = key.match(
      /(brokerMC|dotNumber|mcNumber|provider|scacCode|overrideBrokerMC|overrideDotNumber|overrideMcNumber|overrideScacCode)-(.+)/
    )
    if (match) {
      const [, property, id] = match
      if (!carrierData[id]) {
        carrierData[id] = {
          brokerMC: '',
          dotNumber: '',
          mcNumber: '',
          provider: '',
          scacCode: ''
        }
      }
      carrierData[id][property as TCarrierFormStringKeys] = value
    }
  }

  result.carriers = Object.values(carrierData).map((carrier) => {
    const { overrideBrokerMC, overrideDotNumber, overrideMcNumber, overrideScacCode, ...defaultCarrier } = carrier
    const hasOverride = !!overrideBrokerMC && !!overrideDotNumber && !!overrideMcNumber && !!overrideScacCode

    return {
      ...defaultCarrier,
      ...(hasOverride
        ? {
            truckerToolsOverride: {
              brokerMC: overrideBrokerMC,
              dotNumber: overrideDotNumber,
              mcNumber: overrideMcNumber,
              scacCode: overrideScacCode
            }
          }
        : {})
    }
  })

  return result as { carriers: TCarrier[]; clientID: string; clientSecret: string }
}

export const getTruckerToolsInitialValues = ({
  carriers,
  clientID,
  clientSecret
}: {
  clientID: string
  clientSecret: string
  carriers: TCarrier[]
}): Record<string, string> => {
  const carriersValueMap = carriers.reduce((acc: Record<string, string>, carrier: TCarrier, index: number) => {
    acc[`brokerMC-${index + 1}`] = carrier.brokerMC
    acc[`dotNumber-${index + 1}`] = carrier.dotNumber
    acc[`mcNumber-${index + 1}`] = carrier.mcNumber
    acc[`provider-${index + 1}`] = carrier.provider
    acc[`scacCode-${index + 1}`] = carrier.scacCode

    if (carrier.truckerToolsOverride) {
      acc[`overrideBrokerMC-${index + 1}`] = carrier.truckerToolsOverride.brokerMC
      acc[`overrideDotNumber-${index + 1}`] = carrier.truckerToolsOverride.dotNumber
      acc[`overrideMcNumber-${index + 1}`] = carrier.truckerToolsOverride.mcNumber
      acc[`overrideScacCode-${index + 1}`] = carrier.truckerToolsOverride.scacCode
    }

    return acc
  }, {})
  return {
    clientID,
    clientSecret,
    ...carriersValueMap
  }
}
